import React, {Component} from 'react';
import PropTypes from "prop-types";

import {InputForm} from "../InputForm";

import DataGridHead from "./DataGridHead";
import DataGridRow from "./DataGridRow";
import DataGridFooter from "./DataGridFooter";
import {
    getDeepComponentsByProperty,
    itHasChildrenDeepProperty,
    modifyNestedChildren
} from "../../utils/componentPreparator";

export default class DataGrid extends Component {
    static propTypes = {
        /**
         * make items selectable
         */
        canSelectItems: PropTypes.bool,
        /**
         * can select all items (add checkbox in dataGrid header)
         */
        canSelectAll: PropTypes.bool,
        /**
         * add class to table
         */
        tableAdditionalClass: PropTypes.string,
        /**
         * make table bordered style
         */
        bordered: PropTypes.bool,
        /**
         * add heading with title to dataGrid header
         */
        title: PropTypes.string,
        /**
         * Component inner (should be DataGridHead/DataGridRow/DataGridFooter components)
         * if DataGridRow: components will be placed id tbody tag
         */
        children: PropTypes.oneOfType([
            PropTypes.node,
            PropTypes.shape({
                type: PropTypes.oneOf([DataGridHead, DataGridRow, DataGridFooter]),
            }),
            PropTypes.arrayOf(
                PropTypes.shape({
                    type: PropTypes.oneOf([DataGridHead, DataGridRow, DataGridFooter]),
                })
            ),
        ])
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedItems: [],
            rerender: true,
            selectedAll: false
        };

        this.selectedItems = [];

        this.handleItemSelect = this.handleItemSelect.bind(this);
        this.selectAll = this.selectAll.bind(this);
        this.getSelected = this.getSelected.bind(this);
        this.clearSelected = this.clearSelected.bind(this);

        // layout
        this.getChildrenWithModifiedRows = this.getChildrenWithModifiedRows.bind(this);
        this.getElementsByType = this.getElementsByType.bind(this);
        this.getSelectAllCheckbox = this.getSelectAllCheckbox.bind(this);
        this.getFoot = this.getFoot.bind(this);
        this.getHead = this.getHead.bind(this);
        this.getTableClasses = this.getTableClasses.bind(this);

        this.selectAllCheckboxRef = React.createRef();

        this.dataGridIndex = 0;
    }

    componentDidMount() {
        this.getElementsByType(this.getChildrenOriginalRows(this.props.children), DataGridRow).forEach((child) => {
            if (child.props.selected) {
                this.selectedItems.push(child.props.value);
                this.setState({
                    rerender: true
                })
            }
        })
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.children !== this.props.children) {
            const selectedItems = [];
            this.selectedItems = [];

            this.getElementsByType(this.getChildrenOriginalRows(this.props.children), DataGridRow).forEach((child) => {
                if (child.props.selected && !selectedItems.includes(child.props.value)) {
                    selectedItems.push(child.props.value);
                }
            });

            this.selectedItems = selectedItems;
        }
    }

    async clearSelected() {
        this.selectedItems = [];

        this.setState({
            rerender: true,
            selectedAll: false
        }, () => {
            if (this.props.onItemSelect) {
                this.props.onItemSelect(this.selectedItems);
            }
        })
    }

    async getSelected() {
        return this.state.selectedItems
    }

    async selectAll() {
        let {selectedAll} = this.state;
        let {selectedItems} = this;
        const {children} = this.props;

        const items = this.getElementsByType(this.getChildrenWithModifiedRows(children), DataGridRow);

        if (selectedItems.length !== items.length) {
            selectedItems = [];

            this.getElementsByType(this.getChildrenWithModifiedRows(children), DataGridRow).forEach((child, index) => {
                this[`DataGridRow_${index}`].current.checkboxRef.current.setValue(true);
                selectedItems.push(child.props.value);
            });

            selectedAll = true;
        } else {
            return this.clearSelected();
        }

        this.selectedItems = selectedItems;

        await this.setState({
            selectedAll,
            rerender: true,
        }, () => {
            if (this.props.onItemSelect) {
                this.props.onItemSelect(this.selectedItems);
            }
        });

        return null;
    }

    async handleItemSelect(value) {
        const selectedItems = [...this.selectedItems];

        const items = this.getElementsByType(this.getChildrenWithModifiedRows(this.props.children), DataGridRow);

        if (selectedItems.includes(value)) {
            const index = selectedItems.indexOf(value);
            selectedItems.splice(index, 1)
        } else {
            selectedItems.push(value)
        }

        if (this.props.canSelectAll) {
            if (items.length === selectedItems.length) {
                this.setState({
                    selectedAll: true
                })
            } else if(this.state.selectedAll) {
                this.setState({
                    selectedAll: false
                })
            }
        }

        this.selectedItems = selectedItems;

        if (this.props.onItemSelect) {
            this.props.onItemSelect(this.selectedItems);
        }
    }

    getChildrenWithModifiedRows(children) {
        const {canSelectItems} = this.props;

        this.dataGridIndex = 0;
        return modifyNestedChildren({
            children,
            excludedChildrens: [DataGridHead, DataGridFooter],
            type: DataGridRow,
            beforeReturnCallback: () => {
                if (!this[`DataGridRow_${this.dataGridIndex}`]) {
                    this[`DataGridRow_${this.dataGridIndex}`] = React.createRef();
                }
            },
            onReturnCallback: () => {
                this.dataGridIndex++;
            },
            additionalProps: (child) => {
                return {
                    withCheckbox: canSelectItems,
                    selected: this.selectedItems.includes(child.props.value),
                    onItemSelect: async (value) => {
                        if (child.props.onItemSelect) {
                            child.props.onItemSelect(value);
                        }
                        await this.handleItemSelect(value)
                    },
                    ref: this[`DataGridRow_${this.dataGridIndex}`]
                }
            }
        });
    }

    getChildrenOriginalRows(children) {
        return React.Children.toArray(children).filter((child) => {
            return itHasChildrenDeepProperty(child, 'type', DataGridRow) &&
                !itHasChildrenDeepProperty(child, 'type', DataGridHead) &&
                !itHasChildrenDeepProperty(child, 'type', DataGridFooter);
        });
    }

    getRestChildren(children) {
        return React.Children.toArray(children).filter((child) => {
            return !itHasChildrenDeepProperty(child, 'type', DataGridRow) &&
                !itHasChildrenDeepProperty(child, 'type', DataGridHead) &&
                !itHasChildrenDeepProperty(child, 'type', DataGridFooter);
        });
    }

    getElementsByType(children, type) {
        const rows = [];
        children.forEach((child)=>{
            getDeepComponentsByProperty(child, 'type', type).forEach(e=>rows.push(e))
        });
        return rows;
    }

    getSelectAllCheckbox() {
        const {canSelectAll, canSelectItems} = this.props;

        if(!canSelectItems) {
            return null;
        }

        if(!canSelectAll) {
            return <th/>
        }

        const {selectedAll} = this.state;

        return (
            <th className="data-grid__checkbox">
                <InputForm
                    type={'checkbox'}
                    label={null}
                    value={selectedAll}
                    onClick={async () => {
                        await this.selectAll()
                    }}
                />
            </th>
        );
    }

    getHead(selectAllCheckbox = null) {
        const {children, canSelectItems} = this.props;

        return this.getElementsByType(React.Children.toArray(children), DataGridHead).map((child, index) => {
            return React.cloneElement(child, {
                ...child.props,
                key: index,
                children: child.props.children ? React.Children.map(child.props.children, (cc, ci) => {
                    if (!cc) return null;

                    if(!canSelectItems) return cc;

                    const newChildren =
                        cc.props && cc.props.children ?
                            Array.isArray(cc.props.children) ?
                                [...cc.props.children] :
                                [cc.props.children] :
                            [];

                    // add selectAll checkbox only for first dataGridRow
                    if (ci === 0) {
                        if(!selectAllCheckbox) return cc;

                        newChildren.unshift(selectAllCheckbox);

                        return React.cloneElement(cc, {
                            children: newChildren
                        })
                    }

                    newChildren.unshift(<th/>);

                    return React.cloneElement(cc, {
                        children: newChildren
                    });
                }) : null
            })
        });
    }

    getFoot() {
        const {children} = this.props;
        return this.getElementsByType(React.Children.toArray(children), DataGridFooter);
    }

    getTableClasses() {
        const {canSelectItems, tableAdditionalClass, bordered} = this.props;

        const classes = ['data-grid__body','w-100'];

        if(canSelectItems) {
            classes.push('data-grid--can-select');
        }
        if(tableAdditionalClass) {
            classes.push(tableAdditionalClass)
        }
        if(bordered) {
            classes.push('data-grid--bordered')
        }

        return classes;
    }

    render() {
        const {canSelectItems, canSelectAll, title, children} = this.props;

        if(!children) return '';
        this.dataGridIndex = 0;

        const rows = this.getChildrenWithModifiedRows(children);

        const restComponents = this.getRestChildren(children);

        const selectAllCheckbox = this.getSelectAllCheckbox();

        const head = this.getHead(selectAllCheckbox);

        const foot = this.getFoot();

        const classNames = this.getTableClasses();

        return (
            <div className={'data-grid'}>
                {title ? <div className="data-grid__caption">{title}</div> : null}
                <table
                    className={classNames.join(' ')}>
                    {head && head.length ?
                        head : (canSelectAll && canSelectItems) ?
                            <thead>
                                <tr>
                                    {selectAllCheckbox}
                                </tr>
                            </thead> : null}
                    {rows.length ? (
                        <tbody>
                            {rows}
                        </tbody>
                    ) : null}
                    {restComponents}
                    {foot && foot.length ? foot : null}
                </table>
            </div>
        )
    }
}
